import { EnrichedVideoEditorEditListItemDto } from '@app/modules/video-editions/types';

export interface EditRenderedVideoUrlResult {
  /** Signed video URL when available; null when not rendered or BFF did not return URLs. */
  url: string | null;
  /** Always false: URLs come from list payload (`renderedVideo`), not a separate request. */
  isLoading: boolean;
}

export type GetEditRenderedVideoUrlMode = 'download' | 'playback';

export interface GetEditRenderedVideoUrlOptions {
  /**
   * `download`: BFF `renderedVideo.downloadUrl`.
   * `playback`: BFF `renderedVideo.url` with `response-content-disposition` stripped when present.
   */
  mode: GetEditRenderedVideoUrlMode;
}

/**
 * Removes the `response-content-disposition` query parameter when the storage signed URL
 * still includes it (e.g. so video.js can play inline instead of treating the response as a download).
 */
function withoutResponseContentDispositionQuery(url: string): string {
  try {
    const parsed = new URL(url);
    if (!parsed.searchParams.has('response-content-disposition')) {
      return url;
    }
    parsed.searchParams.delete('response-content-disposition');
    return parsed.toString();
  } catch {
    return url;
  }
}

export type EnrichedEditForRenderedUrl = Pick<
  EnrichedVideoEditorEditListItemDto,
  'renderJob' | 'renderedVideo'
> | null;

/**
 * Returns signed video URLs from BFF-enriched edit payload (`renderedVideo`) when the render job completed.
 */
export function getEditRenderedVideoUrl(
  edit: EnrichedEditForRenderedUrl,
  options: GetEditRenderedVideoUrlOptions,
): EditRenderedVideoUrlResult {
  const { mode } = options;
  const isRenderCompleted = !!edit?.renderJob && edit.renderJob.status === 'COMPLETED';

  if (!edit || !isRenderCompleted || !edit.renderedVideo) {
    return { url: null, isLoading: false };
  }

  const rawUrl = mode === 'download' ? edit.renderedVideo.downloadUrl : edit.renderedVideo.url;

  if (!rawUrl) {
    return { url: null, isLoading: false };
  }

  const url = mode === 'playback' ? withoutResponseContentDispositionQuery(rawUrl) : rawUrl;

  return {
    url,
    isLoading: false,
  };
}
